import { useState, useEffect } from 'react';
import { getParts, generateDemand, getDemandSummary } from '../../services/erpApi';

const DISTRIBUTION_OPTIONS = [
  { value: 'even', label: 'Even Distribution', description: 'Spread evenly across all months' },
  { value: 'front-loaded', label: 'Front-Loaded', description: 'More demand in first half of year' },
  { value: 'back-loaded', label: 'Back-Loaded', description: 'More demand in second half of year' },
];

export default function DemandSimulator() {
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Form state
  const [year, setYear] = useState(new Date().getFullYear());
  const [woPrefix, setWoPrefix] = useState('WO');
  const [demandItems, setDemandItems] = useState([]);
  
  // Preview state
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    loadParts();
  }, []);

  const loadParts = async () => {
    try {
      setLoading(true);
      const data = await getParts();
      setParts(data);
      // Initialize demand items for each part
      setDemandItems(data.map(p => ({
        partId: p.id,
        partNumber: p.partNumber,
        partName: p.name,
        annualDemand: 0,
        batchSize: 100,
        distribution: 'even',
        enabled: false,
      })));
      setError(null);
    } catch (err) {
      setError('Failed to load parts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const updateDemandItem = (partId, field, value) => {
    setDemandItems(items =>
      items.map(item =>
        item.partId === partId ? { ...item, [field]: value } : item
      )
    );
    setPreview(null); // Clear preview when data changes
  };

  const togglePart = (partId) => {
    setDemandItems(items =>
      items.map(item =>
        item.partId === partId ? { ...item, enabled: !item.enabled } : item
      )
    );
    setPreview(null);
  };

  const calculatePreview = () => {
    const enabledItems = demandItems.filter(i => i.enabled && i.annualDemand > 0);
    
    if (enabledItems.length === 0) {
      setError('Select at least one part with demand > 0');
      return;
    }

    let totalWOs = 0;
    let totalQuantity = 0;
    const monthlyWOs = Array(12).fill(0);
    const monthlyQty = Array(12).fill(0);
    const partBreakdown = [];

    for (const item of enabledItems) {
      const numBatches = Math.ceil(item.annualDemand / item.batchSize);
      totalWOs += numBatches;
      totalQuantity += item.annualDemand;

      partBreakdown.push({
        partNumber: item.partNumber,
        partName: item.partName,
        demand: item.annualDemand,
        batches: numBatches,
        batchSize: item.batchSize,
      });

      // Distribute across months
      for (let i = 0; i < numBatches; i++) {
        let month;
        switch (item.distribution) {
          case 'front-loaded':
            month = Math.floor((i / numBatches) * 6);
            break;
          case 'back-loaded':
            month = Math.floor((i / numBatches) * 6) + 6;
            break;
          default: // even
            month = Math.floor((i / numBatches) * 12);
        }
        month = Math.min(month, 11);
        monthlyWOs[month]++;
        monthlyQty[month] += i === numBatches - 1 
          ? item.annualDemand - (item.batchSize * (numBatches - 1))
          : item.batchSize;
      }
    }

    setPreview({
      totalWOs,
      totalQuantity,
      monthlyWOs,
      monthlyQty,
      partBreakdown,
    });
    setError(null);
  };

  const handleGenerate = async () => {
    if (!preview) {
      calculatePreview();
      return;
    }

    const enabledItems = demandItems.filter(i => i.enabled && i.annualDemand > 0);
    
    if (enabledItems.length === 0) {
      setError('Select at least one part with demand > 0');
      return;
    }

    const confirmed = confirm(
      `This will generate ${preview.totalWOs} work orders for ${year}. Continue?`
    );
    if (!confirmed) return;

    try {
      setGenerating(true);
      setError(null);
      
      const demandPlan = enabledItems.map(item => ({
        partId: item.partId,
        annualDemand: parseInt(item.annualDemand),
        batchSize: parseInt(item.batchSize),
        distribution: item.distribution,
      }));

      const result = await generateDemand(demandPlan, year, woPrefix);
      
      setSuccess(`Successfully generated ${result.workOrders.length} work orders!`);
      setPreview(null);
      
      // Reset form
      setDemandItems(items => items.map(i => ({ ...i, enabled: false, annualDemand: 0 })));
      
      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate work orders');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return <div className="text-gray-400">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Demand Simulator</h2>
          <p className="text-gray-400 mt-1">
            Define annual demand for parts and generate work orders automatically
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/20 border border-red-700 rounded p-3 text-red-400">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-900/20 border border-green-700 rounded p-3 text-green-400">
          {success}
        </div>
      )}

      {/* Settings */}
      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold mb-4">Generation Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Target Year</label>
            <input
              type="number"
              value={year}
              onChange={(e) => {
                setYear(parseInt(e.target.value));
                setPreview(null);
              }}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
              min="2024"
              max="2030"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-400 mb-1">WO Number Prefix</label>
            <input
              type="text"
              value={woPrefix}
              onChange={(e) => setWoPrefix(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded focus:border-amber-500 focus:outline-none"
              placeholder="WO"
            />
          </div>
          <div className="flex items-end">
            <p className="text-sm text-gray-500">
              WO numbers will be: {woPrefix}-{year}-0001, {woPrefix}-{year}-0002, ...
            </p>
          </div>
        </div>
      </div>

      {/* Demand Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-lg font-semibold">Part Demand</h3>
          <p className="text-sm text-gray-400">
            Enable parts and set their annual demand and batch sizes
          </p>
        </div>
        
        {parts.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No parts defined. Create parts first in the Parts page.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 w-12">
                  <input
                    type="checkbox"
                    checked={demandItems.every(i => i.enabled)}
                    onChange={(e) => {
                      setDemandItems(items => items.map(i => ({ ...i, enabled: e.target.checked })));
                      setPreview(null);
                    }}
                    className="rounded"
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300">Part</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 w-32">
                  Annual Demand
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 w-28">
                  Batch Size
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 w-40">
                  Distribution
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-300 w-24">
                  # WOs
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {demandItems.map((item) => {
                const numWOs = item.annualDemand > 0 && item.batchSize > 0
                  ? Math.ceil(item.annualDemand / item.batchSize)
                  : 0;
                
                return (
                  <tr
                    key={item.partId}
                    className={`${item.enabled ? 'bg-gray-700/30' : ''} hover:bg-gray-700/50`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={item.enabled}
                        onChange={() => togglePart(item.partId)}
                        className="rounded"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-mono text-amber-400 text-sm">{item.partNumber}</div>
                      <div className="text-gray-400 text-sm">{item.partName}</div>
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={item.annualDemand || ''}
                        onChange={(e) => updateDemandItem(item.partId, 'annualDemand', e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:border-amber-500 focus:outline-none"
                        placeholder="0"
                        min="0"
                        disabled={!item.enabled}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <input
                        type="number"
                        value={item.batchSize || ''}
                        onChange={(e) => updateDemandItem(item.partId, 'batchSize', e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:border-amber-500 focus:outline-none"
                        placeholder="100"
                        min="1"
                        disabled={!item.enabled}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={item.distribution}
                        onChange={(e) => updateDemandItem(item.partId, 'distribution', e.target.value)}
                        className="w-full px-2 py-1 bg-gray-700 border border-gray-600 rounded text-sm focus:border-amber-500 focus:outline-none"
                        disabled={!item.enabled}
                      >
                        {DISTRIBUTION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 text-gray-400">
                      {item.enabled && numWOs > 0 ? numWOs : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Preview Section */}
      {preview && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-6">
          <h3 className="text-lg font-semibold">Generation Preview</h3>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-700/50 rounded p-4">
              <div className="text-3xl font-bold text-amber-400">{preview.totalWOs}</div>
              <div className="text-sm text-gray-400">Total Work Orders</div>
            </div>
            <div className="bg-gray-700/50 rounded p-4">
              <div className="text-3xl font-bold text-green-400">
                {preview.totalQuantity.toLocaleString()}
              </div>
              <div className="text-sm text-gray-400">Total Quantity</div>
            </div>
            <div className="bg-gray-700/50 rounded p-4">
              <div className="text-3xl font-bold text-blue-400">{preview.partBreakdown.length}</div>
              <div className="text-sm text-gray-400">Parts Included</div>
            </div>
            <div className="bg-gray-700/50 rounded p-4">
              <div className="text-3xl font-bold text-purple-400">{year}</div>
              <div className="text-sm text-gray-400">Target Year</div>
            </div>
          </div>

          {/* Monthly Distribution Chart */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-3">Monthly Distribution</h4>
            <div className="grid grid-cols-12 gap-1">
              {preview.monthlyWOs.map((count, i) => {
                const maxWOs = Math.max(...preview.monthlyWOs);
                const height = maxWOs > 0 ? (count / maxWOs) * 100 : 0;
                return (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-full h-20 flex items-end">
                      <div
                        className="w-full bg-amber-500 rounded-t transition-all"
                        style={{ height: `${height}%` }}
                        title={`${count} WOs, ${preview.monthlyQty[i].toLocaleString()} units`}
                      />
                    </div>
                    <span className="text-xs text-gray-400 mt-1">
                      {['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][i]}
                    </span>
                    <span className="text-xs text-gray-500">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Part Breakdown */}
          <div>
            <h4 className="text-sm font-medium text-gray-300 mb-3">Part Breakdown</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {preview.partBreakdown.map((part) => (
                <div key={part.partNumber} className="bg-gray-700/50 rounded p-3">
                  <div className="font-mono text-amber-400 text-sm">{part.partNumber}</div>
                  <div className="text-gray-400 text-sm truncate">{part.partName}</div>
                  <div className="mt-2 text-sm">
                    <span className="text-white">{part.demand.toLocaleString()}</span>
                    <span className="text-gray-500"> units → </span>
                    <span className="text-green-400">{part.batches}</span>
                    <span className="text-gray-500"> WOs ({part.batchSize}/batch)</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4 justify-end">
        <button
          onClick={calculatePreview}
          className="px-6 py-2 bg-gray-600 hover:bg-gray-500 rounded text-white"
          disabled={generating}
        >
          Preview
        </button>
        <button
          onClick={handleGenerate}
          className="px-6 py-2 bg-amber-600 hover:bg-amber-500 rounded text-white disabled:opacity-50"
          disabled={generating || demandItems.filter(i => i.enabled).length === 0}
        >
          {generating ? 'Generating...' : preview ? 'Generate Work Orders' : 'Preview & Generate'}
        </button>
      </div>
    </div>
  );
}
